/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). An agreement's page in the AI
 * assistant (Nami). The agreement's questions are answered by
 * `agent/page-answers.ts`; this file holds the rest:
 * - the chips offered on the page (a mix of questions and jobs);
 * - the jobs: each one explains what it will do and asks to go ahead, then acts
 *   on the page and reports with links (the same follow-up flow as a forecast
 *   change, played by `agent/interceptor.ts`);
 * - a free-text question that is not a chip: answered from the agreement text
 *   when the match is strong enough, with citations, else left to the regular
 *   assistant.
 * Citations are links that highlight the clause in the document on the page.
 */
import { contractSourceLink, sourcesLine } from '../../agent/source-links';
import {
  answerLocally,
  questionCoverage,
  rankSections,
} from '../../ask/local-retrieval';
import { matchPhrasing } from '../../ask/match-scripted';
import type { AskCitation } from '../../ask/types';
import { clausesForContract } from '../../data/clauses';
import { getContract, isContractId } from '../../data/contracts';
import { fmtDate } from '../../data/dates';
import { fmtMoney } from '../../data/format';
import { MATERIALS } from '../../data/materials';
import { getSupplier } from '../../data/suppliers';
import type { ContractId } from '../../data/types';
import { shortTitleFor } from './ask-documents';
import {
  agentActionSpecsFor,
  agentStarters,
  materialNoun,
} from './contract-agent';
import type {
  AgentActionKind,
  AgentActionSpec,
  ComparePlan,
  InvoicesPlan,
  LetterPlan,
  RebidPlan,
  ReminderPlan,
} from './contract-agent';
import { askDocumentsForContract, suggestionsFor } from './contracts-qa';
import { markReminderSet } from './contracts-store';
import type { RenewalEventKind } from './renewals';

/** The most chips offered on one agreement's page. */
const MAX_CHIPS = 6;

/** The agreement a procurement address opens, or undefined for any other page. */
export function contractIdFromPath(pathname: string): ContractId | undefined {
  const [area, section, id, ...rest] = pathname
    .split('/')
    .filter((segment) => segment.length > 0);
  if (
    area !== 'procurement' ||
    section !== 'contracts' ||
    id === undefined ||
    rest.length > 0 ||
    !isContractId(id)
  ) {
    return undefined;
  }
  return id;
}

export interface ContractJob {
  contractId: ContractId;
  kind: AgentActionKind;
  /** The job as a chip: also what the user's message says when they pick it. */
  prompt: string;
  /** Every wording that starts the job: the prompt plus a few others. */
  phrasings: string[];
  /** What Nami will do, then the question to go ahead. */
  proposal: string;
  /** What Nami says once the job is done, with links. */
  report: string;
  /** The tab to open before the job lands, when its change shows on another tab. */
  opens: 'obligations' | null;
  /** The job's one change to the page. */
  apply: () => void;
}

// --- Wording -------------------------------------------------------------------------

const STEP_WORDS = ['one', 'two', 'three', 'four', 'five', 'six'];

function stepsText(spec: AgentActionSpec): string {
  const count = spec.steps.length;
  const words = STEP_WORDS[count - 1] ?? String(count);
  const lines = spec.steps.map(
    (step, index) =>
      `${index + 1}. ${step.label}${step.detail ? ` (${step.detail})` : ''}`,
  );
  return `I will work in ${words} ${count === 1 ? 'step' : 'steps'}:\n\n${lines.join('\n')}`;
}

function link(label: string, href: string): string {
  return `[${label}](${href})`;
}

function contractUrl(contractId: ContractId, search: string): string {
  return `/procurement/contracts/${contractId}?${search}`;
}

/** A link to a clause of this agreement, which highlights it in the document. */
function clauseLink(contractId: ContractId, section: string): string | null {
  const source = contractSourceLink({ documentId: contractId, ref: section });
  return source ? link(source.label, source.href) : null;
}

/** The clause that sets the term and notice of the agreement, as a link. */
function noticeTermsLink(contractId: ContractId): string | null {
  const clause = clausesForContract(contractId).find(
    (entry) => entry.kind === 'auto_renewal' || entry.kind === 'termination',
  );
  return clause ? clauseLink(contractId, clause.section) : null;
}

function calendarOf(recipients: string[]): string {
  const [only] = recipients;
  return recipients.length === 1 && only !== undefined
    ? `${only}'s calendar`
    : `the calendars of ${recipients.join(' and ')}`;
}

/** The date a letter is about, in a sentence. */
const EVENT_SENTENCE: Record<RenewalEventKind, string> = {
  notice_deadline: 'The notice deadline is',
  auto_renews: 'The agreement auto-renews on',
  expires: 'The agreement expires on',
};

/** "Draft the renewal letter" is a job for "the renewal letter". */
function jobNoun(prompt: string): string {
  return prompt.replace(/^Draft the /, '');
}

const ASK_TO_GO_AHEAD =
  'Shall I go ahead? Reply **yes** and I will start, or **no** to leave it.';

function proposalOf(spec: AgentActionSpec, contractId: ContractId): string {
  const contract = getContract(contractId);
  const supplier = getSupplier(contract.supplierId).shortName;
  const title = shortTitleFor(contractId);
  const { plan } = spec;
  const steps = stepsText(spec);
  switch (plan.kind) {
    case 'letter':
      return [
        `I'll draft the ${jobNoun(spec.prompt)} for the ${title}. ${EVENT_SENTENCE[plan.event.kind]} ${fmtDate(plan.event.date)}.`,
        steps,
        `It is a draft for you to read. Nothing is sent to ${supplier}, and it goes to Legal review only if you choose that.`,
        ASK_TO_GO_AHEAD,
      ].join('\n\n');
    case 'reminder':
      return [
        `I'll set a reminder ${plan.leadDays} days before ${plan.targetLabel}, ${fmtDate(plan.targetDate)}.`,
        steps,
        `The reminder fires on ${fmtDate(plan.date)} and goes on ${calendarOf(plan.recipients)}. It also shows under Obligations on this agreement. Nothing is sent to ${supplier}.${plan.note ? ` ${plan.note}` : ''}`,
        ASK_TO_GO_AHEAD,
      ].join('\n\n');
    case 'compare': {
      const noun = materialNoun(MATERIALS[plan.materialId].name);
      return [
        `I'll compare the price in the ${title} with what the other ${noun} suppliers charge.`,
        steps,
        'This only reads prices. Nothing in the agreement or with any supplier changes.',
        ASK_TO_GO_AHEAD,
      ].join('\n\n');
    }
    case 'invoices':
      return [
        `I'll check this year's invoices against the ${title}.`,
        steps,
        'This only reads and reports. It does not hold, release or change any invoice.',
        ASK_TO_GO_AHEAD,
      ].join('\n\n');
    case 'rebid':
      return [
        `I'll start a re-bid draft for the ${title}: ${plan.title}.`,
        steps,
        'It is a draft only. Nothing goes to suppliers until you review the bid request in Sourcing.',
        ASK_TO_GO_AHEAD,
      ].join('\n\n');
  }
}

function letterReport(
  plan: LetterPlan,
  spec: AgentActionSpec,
  contractId: ContractId,
): string {
  const { letter } = plan;
  const terms = noticeTermsLink(contractId);
  return [
    `The ${jobNoun(spec.prompt)} is drafted and waiting for you.`,
    [
      `- **To:** ${letter.to.join(', ')}`,
      `- **Subject:** ${letter.subject}`,
      `- **Delivery:** ${letter.delivery}`,
    ].join('\n'),
    `${link('Open the draft', contractUrl(contractId, `draft=${plan.event.kind}`))} to read the whole letter and send it to Legal review. Nothing goes out until you approve it.${terms ? ` Notice terms: ${terms}.` : ''}`,
  ].join('\n\n');
}

function reminderReport(plan: ReminderPlan, contractId: ContractId): string {
  const terms = noticeTermsLink(contractId);
  const obligations = link(
    'Obligations',
    contractUrl(contractId, 'tab=obligations'),
  );
  return [
    `The reminder is set for ${fmtDate(plan.date)}, ${plan.leadDays} days before ${plan.targetLabel}, ${fmtDate(plan.targetDate)}. It is on ${calendarOf(plan.recipients)} and now shows under ${obligations}.${plan.note ? ` ${plan.note}` : ''}${terms ? ` Notice terms: ${terms}.` : ''}`,
  ].join('\n\n');
}

function compareReport(plan: ComparePlan, contractId: ContractId): string {
  const nameOf = (row: ComparePlan['rows'][number]) =>
    getSupplier(row.supplierId).shortName;
  const lowestPrice = plan.rows.reduce((best, row) =>
    row.unitPrice < best.unitPrice ? row : best,
  );
  const lowestAllIn = plan.rows.reduce((best, row) =>
    row.allIn < best.allIn ? row : best,
  );
  const headline =
    lowestPrice.supplierId === lowestAllIn.supplierId
      ? `${nameOf(lowestPrice)} is lowest on price and on all-in cost.`
      : `${nameOf(lowestPrice)} has the lowest price, ${nameOf(lowestAllIn)} the lowest all-in cost.`;
  const rows = plan.rows.map(
    (row) =>
      `- **${nameOf(row)}**${row.isThis ? ' (this agreement)' : ''}: ${row.share}% of volume, $${row.unitPrice.toFixed(2)} per ${plan.priceUnit}, $${row.allIn.toFixed(2)} all-in`,
  );
  const clause = plan.priceClause
    ? clauseLink(contractId, plan.priceClause.section)
    : null;
  const basis = plan.fullCost
    ? 'All-in cost adds returns, re-spooling, wire-cutting downtime and rush orders to the price, plus freight and duty where they apply.'
    : 'All-in cost adds freight and duty to the price.';
  const compare = link(
    'Open the supplier compare',
    `/procurement/materials/${plan.materialId}`,
  );
  return [
    `**${headline}**`,
    rows.join('\n'),
    basis,
    `${clause ? `Price terms: ${clause}. ` : ''}${compare}.`,
  ].join('\n\n');
}

function invoicesReport(plan: InvoicesPlan): string {
  const { compliance } = plan;
  const latest = compliance.latestInvoice;
  const facts = [
    `- **Invoices checked:** ${compliance.invoicesChecked}`,
    `- **Recovered:** ${fmtMoney(compliance.recovered)}`,
    `- **Still to recover:** ${fmtMoney(compliance.openRecoverable)}`,
  ].join('\n');
  const latestText = latest
    ? `The latest is ${latest.invoiceId} for ${fmtMoney(latest.amount)}:\n\n${latest.findings.map((finding) => `- ${finding}`).join('\n')}`
    : 'Nothing is open on this agreement.';
  const open = latest
    ? link(
        'Open invoice compliance',
        `/procurement/invoice-compliance?invoice=${latest.invoiceId}`,
      )
    : link('Open invoice compliance', '/procurement/invoice-compliance');
  const headline =
    compliance.violationsCaught === 0
      ? `No violations in ${compliance.invoicesChecked} invoices this year.`
      : `${compliance.violationsCaught} ${compliance.violationsCaught === 1 ? 'violation' : 'violations'} caught in ${compliance.invoicesChecked} invoices this year.`;
  return [`**${headline}**`, facts, latestText, `${open}.`].join('\n\n');
}

function rebidReport(plan: RebidPlan): string {
  const sourcing = link('Sourcing', '/procurement/sourcing');
  const compare = plan.materialId
    ? ` ${link('Open the supplier compare', `/procurement/materials/${plan.materialId}`)}.`
    : '';
  return [
    `**Re-bid draft ready: ${plan.title}.**`,
    [
      `- **Baseline:** ${plan.baseline}`,
      `- **Volume:** ${plan.yearlySpend}`,
      `- **Invites:** ${plan.invitees.join(', ')}`,
    ].join('\n'),
    `Nothing goes to suppliers until you review the bid request in ${sourcing}.${compare}`,
  ].join('\n\n');
}

function reportOf(spec: AgentActionSpec, contractId: ContractId): string {
  const { plan } = spec;
  switch (plan.kind) {
    case 'letter':
      return letterReport(plan, spec, contractId);
    case 'reminder':
      return reminderReport(plan, contractId);
    case 'compare':
      return compareReport(plan, contractId);
    case 'invoices':
      return invoicesReport(plan);
    case 'rebid':
      return rebidReport(plan);
  }
}

// --- Jobs ----------------------------------------------------------------------------

function toJob(spec: AgentActionSpec, contractId: ContractId): ContractJob {
  const remindsOnPage = spec.kind === 'reminder';
  return {
    contractId,
    kind: spec.kind,
    prompt: spec.prompt,
    phrasings: [spec.prompt, ...spec.aliases],
    proposal: proposalOf(spec, contractId),
    report: reportOf(spec, contractId),
    opens: remindsOnPage ? 'obligations' : null,
    apply: remindsOnPage ? () => markReminderSet(contractId) : () => undefined,
  };
}

const jobCache = new Map<ContractId, ContractJob[]>();

/** The jobs the assistant can run on this agreement, in the order they are offered. */
export function contractJobsFor(contractId: ContractId): ContractJob[] {
  const cached = jobCache.get(contractId);
  if (cached) return cached;
  const built = agentActionSpecsFor(contractId).map((spec) =>
    toJob(spec, contractId),
  );
  jobCache.set(contractId, built);
  return built;
}

/**
 * The chips on an agreement's page: the curated few first (questions and jobs
 * mixed), then the rest of its questions and jobs in turn, up to six.
 */
export function contractChips(contractId: ContractId): string[] {
  const questions = suggestionsFor(contractId);
  const specs = agentActionSpecsFor(contractId);
  const starters = agentStarters(contractId, questions, specs);
  const rest: string[] = [];
  const moreQuestions = questions.map((entry) => entry.question);
  const moreJobs = specs.map((spec) => spec.prompt);
  for (
    let index = 0;
    index < Math.max(moreQuestions.length, moreJobs.length);
    index += 1
  ) {
    const question = moreQuestions[index];
    const job = moreJobs[index];
    if (question !== undefined) rest.push(question);
    if (job !== undefined) rest.push(job);
  }
  return [...new Set([...starters, ...rest])].slice(0, MAX_CHIPS);
}

// --- The conversation ----------------------------------------------------------------

export type ContractTurn =
  /** Nami explains the job and waits for a go-ahead. */
  | { kind: 'ask'; narration: string; job: ContractJob }
  /** The go-ahead came: Nami acts, then reports. */
  | { kind: 'run'; narration: string; job: ContractJob }
  /** Nami only answers (a "no", or an answer from the agreement). */
  | { kind: 'reply'; narration: string };

function words(message: string): string[] {
  return message
    .toLowerCase()
    .replace(/[^a-z\s']/g, ' ')
    .replace(/'/g, '')
    .split(/\s+/)
    .filter((word) => word.length > 0);
}

const YES_WORDS = new Set([
  'yes',
  'yep',
  'yeah',
  'yup',
  'sure',
  'ok',
  'okay',
  'proceed',
  'confirm',
  'continue',
  'approve',
  'approved',
]);
const YES_PHRASES = [
  'go ahead',
  'go for it',
  'do it',
  'please do',
  'sounds good',
  'looks good',
  'lets do it',
  'that works',
];
const NO_WORDS = new Set(['no', 'nope', 'nah', 'cancel', 'stop', 'dont']);
const NO_PHRASES = [
  'never mind',
  'nevermind',
  'not now',
  'leave it',
  'hold off',
];
/** A reply to "shall I go ahead?" is short; a longer message is a new request. */
const MAX_REPLY_WORDS = 5;

/** Whether a short message says yes or no to a question, or neither. */
export function readGoAhead(message: string): 'yes' | 'no' | null {
  const spoken = words(message);
  const [first] = spoken;
  if (first === undefined || spoken.length > MAX_REPLY_WORDS) return null;
  const text = spoken.join(' ');
  if (
    NO_WORDS.has(first) ||
    NO_PHRASES.some((phrase) => text.startsWith(phrase))
  ) {
    return 'no';
  }
  if (
    YES_WORDS.has(first) ||
    YES_PHRASES.some((phrase) => text.includes(phrase))
  ) {
    return 'yes';
  }
  return null;
}

/** The job a message starts on this agreement's page, explained and waiting for a go-ahead. */
export function planContractJob(
  message: string,
  pathname: string,
): ContractTurn | undefined {
  const contractId = contractIdFromPath(pathname);
  if (!contractId) return undefined;
  const job = matchPhrasing(
    message,
    contractJobsFor(contractId),
    (candidate) => candidate.phrasings,
  );
  return job ? { kind: 'ask', narration: job.proposal, job } : undefined;
}

/**
 * The answer to "shall I go ahead?". A yes runs the job; a no leaves it; any
 * other message is not an answer, and the caller handles it as a new message.
 */
export function planJobReply(
  pending: ContractJob,
  message: string,
  pathname: string,
): ContractTurn | undefined {
  if (contractIdFromPath(pathname) !== pending.contractId) return undefined;
  const answer = readGoAhead(message);
  if (answer === 'yes') {
    return { kind: 'run', narration: pending.report, job: pending };
  }
  if (answer === 'no') {
    return {
      kind: 'reply',
      narration: 'Okay, I will leave it. Nothing was changed.',
    };
  }
  return undefined;
}

function citationLink(citation: AskCitation) {
  return contractSourceLink(citation);
}

/**
 * A local answer must clear both bars, or the regular assistant answers
 * instead. The score keeps out matches on one stray word ("the weather in
 * Dallas"); the coverage keeps out questions about things the agreement never
 * mentions.
 */
const MIN_LOCAL_SCORE = 4.4;
const MIN_QUESTION_COVERAGE = 0.5;

/**
 * An answer built from the agreement's own text, for a question that is not one
 * of the chips, or undefined when the match is too weak to answer. Cites the
 * sections it used, each a link that highlights the clause.
 */
export function localContractAnswer(
  message: string,
  pathname: string,
): string | undefined {
  const contractId = contractIdFromPath(pathname);
  if (!contractId) return undefined;
  const documents = askDocumentsForContract(contractId);
  const top = rankSections(message, documents)[0];
  if (
    !top ||
    top.score < MIN_LOCAL_SCORE ||
    questionCoverage(message, documents) < MIN_QUESTION_COVERAGE
  ) {
    return undefined;
  }
  const local = answerLocally(message, documents);
  if (local.confidence === 'low' || local.citations.length === 0) {
    return undefined;
  }
  return `${local.answer}${sourcesLine(local.citations.map(citationLink))}`;
}
